import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Product, ProductListItem } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { cartApi } from '../../services/cartApi';
import ProductQuickViewModal from './ProductQuickViewModal';
import './ProductCard.css';

interface ProductCardProps {
  product: ProductListItem;
  onClick?: (productId: string) => void;
}

/**
 * 商品卡片元件
 * 參考 AdCard 結構，展示商品資訊
 */
const ProductCard = ({ product, onClick }: ProductCardProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showQuickView, setShowQuickView] = useState(false);

  const handleQuickView = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering card navigation
    setShowQuickView(true);
  };

  const handleCloseQuickView = () => {
    setShowQuickView(false);
  };

  const handleAddToCart = async (product: Product | ProductListItem) => {
    try {
      await cartApi.addToCart({
        productId: product.id,
        quantity: 1,
      });
      // TODO: Show success toast notification
      handleCloseQuickView();
    } catch (error) {
      console.error('加入購物車失敗:', error);
      // TODO: Show error toast notification
    }
  };

  const handleBuy = async (product: Product | ProductListItem) => {
    // TODO: Integrate with checkout
    console.log('Buy now:', product);
    handleCloseQuickView();
  };

  const handleEdit = (product: Product | ProductListItem) => {
    handleCloseQuickView();
    // Navigate to admin product edit page
    navigate(`/admin/products/${product.id}/edit`);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering image click
    if (onClick) {
      onClick(product.id);
    }
  };

  // 取得顯示價格（優先顯示特價，否則顯示原價）
  const displayPrice = product.salePrice || product.regularPrice;
  const hasSalePrice = product.salePrice !== null && product.regularPrice !== null && product.salePrice < product.regularPrice;

  // 圖片 URL
  const imageUrl = product.featuredImageUrl || (product.imageUrls && product.imageUrls.length > 0 ? product.imageUrls[0] : null);

  // 庫存狀態顯示
  const getStockStatusText = () => {
    switch (product.stockStatus) {
      case 'instock':
        return '有庫存';
      case 'outofstock':
        return '已售完';
      case 'onbackorder':
        return '補貨中';
      default:
        return '';
    }
  };

  return (
    <>
      <article
        className={`product-card ${product.featured ? 'product-card--featured' : ''}`}
      >
        {product.featured ? <span className="product-card__badge">精選</span> : null}

        {imageUrl && (
          <div
            className="product-card__image"
            onClick={handleQuickView}
            title="點擊查看商品詳情"
            style={{ cursor: 'pointer' }}
          >
            <img
              src={imageUrl}
              alt={product.title}
              loading="lazy"
            />
            {product.stockStatus === 'outofstock' && (
              <div className="product-card__sold-out">已售完</div>
            )}
          </div>
        )}

        <div className="product-card__content">
          <h3 className="product-card__title">{product.title}</h3>

          {product.shortDescription && (
            <p className="product-card__summary">{product.shortDescription}</p>
          )}

          <div className="product-card__category">
            <span>📦 {product.productCategory.split(',').map(c => c.trim()).filter(c => c)[0] || product.productCategory}</span>
          </div>

          {displayPrice && (
            <div className="product-card__price">
              {hasSalePrice && (
                <span className="product-card__price--original">
                  NT${product.regularPrice}
                </span>
              )}
              <span className="product-card__price--current">
                NT${displayPrice}
              </span>
            </div>
          )}

          {product.tags && product.tags.length > 0 && (
            <div className="product-card__tags">
              {product.tags.slice(0, 3).map((tag, index) => (
                <span key={index} className="product-card__tag">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="product-card__footer">
            <div className="product-card__stock">
              {getStockStatusText()}
            </div>
            <button
              className="product-card__cta"
              onClick={handleButtonClick}
            >
              查看詳情
            </button>
          </div>
        </div>
      </article>

      {/* ProductQuickViewModal */}
      {showQuickView && (
        <ProductQuickViewModal
          product={product}
          mode="customer"
          isOpen={showQuickView}
          onClose={handleCloseQuickView}
          onAddToCart={handleAddToCart}
          onBuy={handleBuy}
          onEdit={handleEdit}
          userRole={user?.role === 'moderator' ? 'admin' : user?.role as 'admin' | 'member' | 'super_admin' | undefined}
        />
      )}
    </>
  );
};

export default ProductCard;
